import { EmptyStateNoResults, ThirdPartyLogo, StepCard } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { ActionTemplateCategoryResource, ActionTemplateSearchResource, ProcessTemplateVersionSummary } from "@octopusdeploy/octopus-server-client";
import { ActionHandlerCategory } from "@octopusdeploy/octopus-server-client";
import type { LinkHref } from "@octopusdeploy/portal-routes";
import { useState } from "react";
import semver from "semver/preload";
import { Action, ProcessTemplateConsumerAction, useAnalyticProcessTemplateConsumerActionDispatch } from "~/analytics/Analytics";
import { PlatformHubUpsellContent } from "~/areas/platformHub/PlatformHubUpsellContent";
import { renderCategoryTemplates, renderFilteredTemplates } from "~/areas/projects/components/Process/ActionTemplateSelector/ActionTemplateHelpers";
import { ProcessTemplatePreviewDrawer } from "~/areas/projects/components/Process/ActionTemplateSelector/ProcessTemplatePreviewDrawer";
import { ProcessTemplateSelectionWizardDialog } from "~/areas/projects/components/Process/ActionTemplateSelector/ProcessTemplateSelectionWizardDialog";
import { useProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { useProcessQueryStringContext } from "~/areas/projects/components/Process/Contexts/ProcessQueryString/ProcessQueryStringContext";
import { isDeploymentOrRunbookProcessIdentifier, isProjectTemplateProcessIdentifier, isTemplatedProjectProcessIdentifier, getProcessTemplateConsumerResourceType } from "~/areas/projects/components/Process/types";
import { UpdateBehaviour } from "~/areas/projects/components/Templates/TemplateVersioningSection";
import filterActionTemplates from "~/components/ActionTemplates/filterActionTemplates";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import Markdown from "~/components/Markdown";
import { resolveUrl } from "~/utils/resolveUrl";
import styles from "./InstalledActionTemplateList.module.less";
interface ProcessTemplateActionTemplateListProps {
    templates: ActionTemplateSearchResource[];
    categories: ActionTemplateCategoryResource[];
    filter?: string;
    activeCategory?: ActionHandlerCategory;
    onPostSelectionUrlRequested: (template: ActionTemplateSearchResource) => LinkHref;
}
export function ProcessTemplateActionTemplateList({ templates, activeCategory, filter }: ProcessTemplateActionTemplateListProps) {
    const processContext = useProcessContext();
    const processIdentifier = processContext.state.processIdentifier;
    if (!isDeploymentOrRunbookProcessIdentifier(processIdentifier) && !isProjectTemplateProcessIdentifier(processIdentifier) && !isTemplatedProjectProcessIdentifier(processIdentifier)) {
        throw new Error("Process templates can only be used in deployment, runbook, or project template processes.");
    }
    const projectId = isDeploymentOrRunbookProcessIdentifier(processIdentifier) || isTemplatedProjectProcessIdentifier(processIdentifier) ? processIdentifier.projectId : undefined;
    const processQueryStringContext = useProcessQueryStringContext();
    const processTemplateConsumerActionDispatch = useAnalyticProcessTemplateConsumerActionDispatch(projectId ?? "", getProcessTemplateConsumerResourceType(processIdentifier));
    const { openDialog: openAddProcessTemplateDialog, isOpen: isProcessTemplateDialogOpen, closeAllDialogs: closeAddProcessTemplateDialog } = useDialogTrigger();
    const [selectedProcessTemplateActionTemplate, setSelectedProcessTemplateActionTemplate] = useState<ActionTemplateSearchResource | undefined>();
    const [processTemplateVersionSummary, setProcessTemplateVersionSummary] = useState<ProcessTemplateVersionSummary | undefined>();
    const { openDialog: openPreviewDrawer, isOpen: isPreviewDrawerOpen, closeAllDialogs: closePreviewDrawer } = useDialogTrigger();
    const [previewTemplate, setPreviewTemplate] = useState<ActionTemplateSearchResource | undefined>();
    const { result: licenseFeatures, error, isLoading: isLoadingLicense } = useInlineStatusQuery(async (repository) => await repository.Licenses.getFeatures(), [], "Get licensed features");
    const isProcessTemplatesLicensed = licenseFeatures ? licenseFeatures.EnabledFeatures.includes("ProcessTemplates") : undefined;
    const handleOpenProcessTemplateDialog = (template: ActionTemplateSearchResource, versionSummary: ProcessTemplateVersionSummary) => {
        setSelectedProcessTemplateActionTemplate(template);
        setProcessTemplateVersionSummary(versionSummary);
        openAddProcessTemplateDialog();
    };
    const handleCloseProcessTemplateDialog = () => {
        setSelectedProcessTemplateActionTemplate(undefined);
        setProcessTemplateVersionSummary(undefined);
        closeAddProcessTemplateDialog();
    };
    const handleOpenPreviewDrawer = (template: ActionTemplateSearchResource) => {
        setPreviewTemplate(template);
        openPreviewDrawer();
    };
    const handleClosePreviewDrawer = () => {
        setPreviewTemplate(undefined);
        closePreviewDrawer();
    };
    const distinctTemplates = getDistinctTemplates(templates);
    const categoryTemplates = activeCategory === ActionHandlerCategory.ProcessTemplate ? distinctTemplates : [];
    const isFiltering = !!filter;
    const filteredResults = filterActionTemplates(distinctTemplates, filter ?? "");
    if (activeCategory === ActionHandlerCategory.ProcessTemplate && !isLoadingLicense && !isProcessTemplatesLicensed) {
        const description = (<div>
                <p>Process Templates are reusable, standardized blocks of steps for deployment processes or runbooks.</p>
            </div>);
        return (<PlatformHubUpsellContent title="Standardize your deployment processes and enforce policies with Process Templates" description={description} featureName="Process Templates" onRequestTrial={async (repository) => {
                await repository.PlatformHub.requestTrial();
            }} compact={true}/>);
    }
    if (activeCategory === ActionHandlerCategory.ProcessTemplate && !isLoadingLicense && isProcessTemplatesLicensed && distinctTemplates.length === 0) {
        return <EmptyStateNoResults />;
    }
    return (<>
            <div className={styles.container}>
                <>
                    {renderCategoryTemplates({
            categoryTemplates,
            activeCategory,
            sectionHeader: `Process Templates (${categoryTemplates.length})`,
            renderSingleTemplate: (template: ActionTemplateSearchResource) => renderStepCard(template),
        })}
                </>
                {isFiltering &&
            renderFilteredTemplates({
                filteredResults,
                sectionHeader: `Process Templates (${filteredResults.length})`,
                renderSingleTemplate: (template: ActionTemplateSearchResource) => renderStepCard(template),
                transitionKey: "filteredProcessTemplates",
            })}
            </div>
            {selectedProcessTemplateActionTemplate && processTemplateVersionSummary && (<ProcessTemplateSelectionWizardDialog open={isProcessTemplateDialogOpen} template={selectedProcessTemplateActionTemplate} versionSummary={processTemplateVersionSummary} onAddTemplate={async (templateId: string, versionMask: string) => {
                processQueryStringContext.actions.addProcessTemplateStep(templateId, versionMask);
                handleCloseProcessTemplateDialog();
            }} onClose={handleCloseProcessTemplateDialog} processTemplateConsumerActionDispatch={processTemplateConsumerActionDispatch}/>)}
            {previewTemplate && <ProcessTemplatePreviewDrawer isOpen={isPreviewDrawerOpen} onClose={handleClosePreviewDrawer} template={previewTemplate} availableVersions={getAvailableVersions(previewTemplate)}/>}
        </>);
    function getPrimaryAction(template: ActionTemplateSearchResource) {
        const openProcessTemplateDialog = () => {
            const availableVersions = getAvailableVersions(template);
            const stableVersion = availableVersions?.find((v) => !semver.prerelease(v)) || null;
            const prereleaseVersion = availableVersions?.find((v) => semver.prerelease(v)) || null;
            const versionSummary: ProcessTemplateVersionSummary = {
                ProcessTemplateSlug: template.Id,
                LatestStableVersion: stableVersion,
                LatestPreReleaseVersion: prereleaseVersion,
                IsOnlyPreRelease: template.Prerelease,
            };
            handleOpenProcessTemplateDialog(template, versionSummary);
        };
        const addProcessTemplate = () => {
            processQueryStringContext.actions.addProcessTemplateStep(template.Id, "PreRelease");
            processTemplateConsumerActionDispatch("Process Template Version Preferences Set", {
                type: "Process Template Version Preferences Set",
                action: ProcessTemplateConsumerAction.SelectVersionPreference,
                processTemplateId: template.Id,
                versioningPreference: UpdateBehaviour.PreRelease,
                isPreRelease: true,
            });
            processTemplateConsumerActionDispatch("Add Process Template To Process", {
                type: "Add Process Template To Process",
                processTemplateId: template.Id,
                action: Action.Add,
            });
        };
        return template.Prerelease
            ? {
                label: "Add Template",
                onClick: addProcessTemplate,
            }
            : {
                label: "Add Template",
                onClick: openProcessTemplateDialog,
            };
    }
    function getAvailableVersions(template: ActionTemplateSearchResource): string[] {
        const stableVersion = templates.find((t) => t.Id === template.Id && !t.Prerelease)?.Version;
        const preReleaseVersion = templates.find((t) => t.Id === template.Id && t.Prerelease)?.Version;
        return [stableVersion, preReleaseVersion].filter((v): v is string => !!v);
    }
    function renderStepCard(template: ActionTemplateSearchResource) {
        const availableVersions = getAvailableVersions(template);
        return (<StepCard key={template.Type + template.Id} heading={template.Name} subHeading={availableVersions.join(", ") ?? ""} logo={<ThirdPartyLogo url={resolveUrl(template.Links.Logo)} size={space[40]} isSquare={true}/>} chipLabel="Process Template" chipType="info" description={<Markdown markup={template.Description} noMargins mutedTextColor/>} dotpoints={template.Features} truncateDotpoints={true} primaryAction={getPrimaryAction(template)} secondaryAction={{
                label: "Preview",
                onClick: () => handleOpenPreviewDrawer(template),
            }}/>);
    }
}
function getDistinctTemplates(templates: ActionTemplateSearchResource[]): ActionTemplateSearchResource[] {
    const map = new Map<string, ActionTemplateSearchResource>();
    for (const template of templates) {
        const existing = map.get(template.Id);
        //preference viewing stable over pre-release version
        if (!existing || (!template.Prerelease && existing.Prerelease)) {
            map.set(template.Id, template);
        }
    }
    return Array.from(map.values());
}
